// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Torc — every colour that carries meaning, in one place so tests/contrast.test.ts can hold it to
// WCAG 2.1 §1.4.11 (≥3:1 for a non-text graphic) against every surface it can actually sit on.
//
// THE GOVERNING SPLIT: the field is dark metalwork and everything that matters on it is BRIGHT. The
// lattice lines are held to the same 3:1 bar as the pieces, which is not fussiness — on this board
// "which points are on one line" IS the rule, so a line you cannot see is a rule you cannot read.
//
// AMBER AND TEAL ARE ALL BUT ISOLUMINANT (1.08:1 against each other), and that is deliberate: it
// means neither side reads as the "strong" colour, and it is the classic orange/cyan pair that
// survives protanopia and deuteranopia. But it also means COLOUR ALONE CANNOT TELL THE TWO SIDES
// APART IN GREYSCALE, so silhouette does the work instead — an Amber stud is a CIRCLE and a Teal
// stud is a HEXAGON. Same for the two piece classes: a stud is a filled disc and a torc is an open
// annulus, which is why torcAmber sits only 1.31:1 from amber and does not need to do better.
// Delete the shapes and the palette becomes a lie; the contrast test says so in words.

export const PALETTE = {
  night: '#0B0F14',
  panel: '#161D26',
  panelHi: '#212B36',

  /** The playing field, and the slightly lighter disc under the lattice. */
  field: '#151B23',
  fieldHi: '#1D2530',
  /** The border ring around the field. */
  rim: '#0E131A',

  /** The lines between points. Structure, and on this board structure is meaning. */
  lattice: '#6E8296',
  /** An empty point. */
  point: '#8496A8',

  /** Studs. */
  amber: '#F0A63C',
  teal: '#3FBFC4',
  /** Torcs — same hue, brighter, and an ANNULUS rather than a disc. */
  torcAmber: '#FFC46B',
  torcTeal: '#6FE0E4',

  /** The torc you have picked up. */
  pick: '#FFFFFF',
  /** Where it may ride. Violet on purpose — green would collide with teal under deuteranopia, and
   *  amber-red would collide with the Amber player under protanopia. */
  target: '#C9A9FF',
  /** Studs that just turned over. */
  flip: '#FF7FAC',
  /** The last ride's from/to trail. */
  last: '#FFD98A',

  text: '#E9EFF5',
  textMuted: '#A5B4C2',
  gold: '#F0A63C',
  alarm: '#FF9B76',
} as const;

/** Every surface a meaningful mark is actually DRAWN ON. */
export const SURFACES = ['field', 'fieldHi', 'rim'] as const;

/**
 * Which surfaces each mark can ACTUALLY sit on. A blanket cross-product would be a lie in both
 * directions — working this map out honestly is most of the value of the gate.
 */
export const MARK_SURFACES: Record<string, ReadonlyArray<string>> = {
  amber: ['field', 'fieldHi'],
  teal: ['field', 'fieldHi'],
  torcAmber: ['field', 'fieldHi'],
  torcTeal: ['field', 'fieldHi'],
  lattice: ['field', 'fieldHi'],
  point: ['field', 'fieldHi'],
  pick: ['field', 'fieldHi'],
  target: ['field', 'fieldHi'],
  flip: ['field', 'fieldHi'],
  last: ['field', 'fieldHi'],
};

/** Everything that has to be findable, in the order the test reports them. */
export const ON_BOARD = [
  'amber',
  'teal',
  'torcAmber',
  'torcTeal',
  'lattice',
  'point',
  'pick',
  'target',
  'flip',
  'last',
] as const;

export const SEAT_COLOUR = [PALETTE.amber, PALETTE.teal];
export const SEAT_TORC = [PALETTE.torcAmber, PALETTE.torcTeal];
export const SEAT_NAME = ['Amber', 'Teal'];
/** The channel that survives any colour vision and any screenshot. */
export const SEAT_SHAPE = ['circle', 'hexagon'];
